import React from 'react';

import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { useAppSelector } from '@core/redux/hooks';
import { getLineCategory } from '@utils/helper-utils';

import { FormValues } from '../types';

import { useFormikContext } from 'formik';
import { LineCategory } from 'src/enums';

type Props = {
  status: string;
  currencySymbol: string;
  lineId: string;
  lineCode: string;
  showChargesFields: boolean;
};

const CancellationCostChargesWidgetAtom: React.FC<Props> = ({
  status,
  currencySymbol,
  lineId,
  lineCode,
  showChargesFields
}) => {
  const user = useAppSelector((state) => state.user);
  const { values, handleBlur, setFieldValue, touched, errors } =
    useFormikContext<FormValues>();

  const s = String(status || '').toUpperCase();
  const isLocked = s === 'CONFIRMED' || s === 'CANCELLED';
  const canEditLine = user?.info?.editableChargesLines?.find?.(
    (i: any) => i?.lineID === lineId
  );

  const disabledRow = isLocked || !canEditLine;
  const isExpat = getLineCategory(lineCode) === LineCategory.Expat;

  return (
    <WidgetPaper>
      <WidgetSection title="Cost & Charges" isFieldsGrid fieldsPerRow={1}>
        {/* Basic Premium fields - only show for line code 43 */}
        {isExpat && (
          <WidgetSection isFieldsGrid fieldsPerRow={2}>
            <CurrencyFormField
              key="chargesAndCost.basicPremium"
              name="chargesAndCost.basicPremium"
              title="Basic Premium*"
              placeholder="Basic Premium"
              value={values?.chargesAndCost?.basicPremium}
              onChange={() => {}}
              currencySymbol={currencySymbol}
              disabled
              material
            />
            <CurrencyFormField
              key="chargesAndCost.basicPremiumVariance"
              name="chargesAndCost.basicPremiumVariance"
              title="Basic Premium Variance*"
              placeholder="Basic Premium Variance"
              value={values?.chargesAndCost?.basicPremiumVariance}
              onBlur={(e) => handleBlur(e)}
              onChange={(e) =>
                setFieldValue(
                  'chargesAndCost.basicPremiumVariance',
                  e.target.value
                )
              }
              error={
                touched?.chargesAndCost?.basicPremiumVariance
                  ? errors?.chargesAndCost?.basicPremiumVariance
                  : ''
              }
              disabled={disabledRow}
              maxValue={0}
              minValue={values?.chargesAndCost?.basicPremium * -1}
              currencySymbol={currencySymbol}
              material
              allowNegative
            />
          </WidgetSection>
        )}

        {/* Charges Amount & TPA Fees fields */}
        {showChargesFields && (
          <>
            <WidgetSection isFieldsGrid fieldsPerRow={2}>
              <CurrencyFormField
                key="chargesAndCost.chargesAmount"
                name="chargesAndCost.chargesAmount"
                title="Charges Amount*"
                placeholder="Charges Amount"
                value={values?.chargesAndCost?.chargesAmount}
                onChange={() => {}}
                disabled
                material
                currencySymbol={currencySymbol}
              />
              <CurrencyFormField
                key="chargesAndCost.chargesAmountVariance"
                name="chargesAndCost.chargesAmountVariance"
                title="Charges Amount Variance*"
                placeholder="Charges Amount Variance"
                value={values?.chargesAndCost?.chargesAmountVariance}
                onBlur={(e) => handleBlur(e)}
                onChange={(e) => {
                  setFieldValue(
                    'chargesAndCost.chargesAmountVariance',
                    e.target.value
                  );
                }}
                error={
                  touched?.chargesAndCost?.chargesAmountVariance
                    ? errors?.chargesAndCost?.chargesAmountVariance
                    : ''
                }
                disabled={disabledRow}
                maxValue={0}
                minValue={values?.chargesAndCost?.chargesAmount * -1}
                currencySymbol={currencySymbol}
                material
                allowNegative
              />
            </WidgetSection>

            <WidgetSection isFieldsGrid fieldsPerRow={2}>
              <CurrencyFormField
                key="chargesAndCost.tpaFees"
                name="chargesAndCost.tpaFees"
                title="TPA Fees*"
                placeholder="TPA Fees"
                value={values?.chargesAndCost?.tpaFees}
                onChange={() => {}}
                currencySymbol={currencySymbol}
                disabled
                material
              />
              <CurrencyFormField
                key="chargesAndCost.tpaFeesVariance"
                name="chargesAndCost.tpaFeesVariance"
                title="TPA Fees Variance*"
                placeholder="TPA Fees Variance"
                value={values?.chargesAndCost?.tpaFeesVariance}
                error={
                  touched?.chargesAndCost?.tpaFeesVariance
                    ? errors?.chargesAndCost?.tpaFeesVariance
                    : ''
                }
                onBlur={(e) => handleBlur(e)}
                onChange={(e) =>
                  setFieldValue('chargesAndCost.tpaFeesVariance', e.target.value)
                }
                disabled={disabledRow}
                maxValue={0}
                minValue={values?.chargesAndCost?.tpaFees * -1}
                currencySymbol={currencySymbol}
                material
                allowNegative
              />
            </WidgetSection>
          </>
        )}

        {/* Cancellation Cost */}
        <WidgetSection isFieldsGrid fieldsPerRow={1}>
          <CurrencyFormField
            key="chargesAndCost.cancellationCost"
            name="chargesAndCost.cancellationCost"
            title="Cancellation Cost*"
            placeholder="Cancellation Cost"
            value={values?.chargesAndCost?.cancellationCost}
            onBlur={(e) => handleBlur(e)}
            onChange={(e) =>
              setFieldValue('chargesAndCost.cancellationCost', e.target.value)
            }
            error={
              touched?.chargesAndCost?.cancellationCost
                ? errors?.chargesAndCost?.cancellationCost
                : ''
            }
            disabled={disabledRow}
            material
            minValue={0}
            currencySymbol={currencySymbol}
          />
        </WidgetSection>
      </WidgetSection>
    </WidgetPaper>
  );
};

export { CancellationCostChargesWidgetAtom };
